import { ref, useId } from 'vue';

/**
 * Owns the single person-panel instance for a view.
 *
 * Hoisted to the view rather than the link site so that only one panel can ever be
 * open, no matter which of the many triggers (tray author, roster, comment authors)
 * was clicked.
 */
export function usePersonPanel() {
  const selectedProfileUuid = ref(null);
  const triggerEl = ref(null);

  // Owned here rather than inside PersonPanel so triggers can point aria-controls at
  // the panel before it exists.
  const panelId = `person-panel-${useId()}`;

  const openPersonPanel = (uuid, buttonEl) => {
    if (!uuid) return;

    // Clicking the open panel's own trigger again toggles it shut, and focus stays on
    // that trigger.
    if (selectedProfileUuid.value === uuid) {
      closePersonPanel('toggle');

      return;
    }

    selectedProfileUuid.value = uuid;
    triggerEl.value = buttonEl ?? null;
  };

  /**
   * Focus returns to the trigger unless the user closed the panel by clicking elsewhere,
   * in which case focus belongs wherever they just clicked. A failed fetch also restores:
   * the panel vanishes on its own, and leaving focus on the removed element would drop
   * the keyboard user out to <body> and lose their place in the page.
   */
  const closePersonPanel = reason => {
    const shouldRestoreFocus = reason !== 'outside-click';
    const previousTrigger = triggerEl.value;

    selectedProfileUuid.value = null;
    triggerEl.value = null;

    if (shouldRestoreFocus && previousTrigger?.isConnected) {
      previousTrigger.focus();
    }
  };

  return { selectedProfileUuid, triggerEl, panelId, openPersonPanel, closePersonPanel };
}
